import { useReducer, useMemo, useCallback } from 'react'
import { toast } from 'sonner'
import { workflowsApi, presentationsApi, materialsApi } from '@/lib/api'
import { playgroundErrorMessage } from './playgroundError'
import type {
  Workflow, WorkflowNode, WorkflowEdge, WorkflowNodeData,
  NodeType, ReplyScenario, PreviousEmail,
} from '@/types/workflow'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UsageEntry {
  agent: string
  input_tokens: number
  output_tokens: number
  model: string
  cost: number
}

export interface GeneratedEmail {
  subject: string
  body: string
  blocks?: { name: string; content: string }[]
  contact_name?: string
  discussion?: { role: string; content: string }[]
  is_goodbye?: boolean
  email_step?: number
  total_emails?: number
  usage_log?: UsageEntry[]
}

export interface GeneratedDeck {
  title: string
  slides: any[]
  color_theme?: Record<string, string>
  discussion?: { role: string; content: string }[]
  contact_name?: string
  usage_log?: UsageEntry[]
  download_url?: string
  filename?: string
  // Set when the node ran in attach_direct mode — no LLM, just a material preview.
  attached_material_id?: number
  content_type?: string
  // Carried from the node's generate_deck_config so a later render (Download,
  // or materializing this deck as a Send Email test attachment) still applies
  // the configured logo — /preview itself doesn't bake the logo into the
  // slide JSON, only the render step does.
  logo_url?: string
}

export interface PlaygroundStep {
  nodeId: string
  nodeType: NodeType
  nodeData: WorkflowNodeData
  status: 'pending' | 'active' | 'generating' | 'completed' | 'skipped'
  /**
   * Human-readable reason the last generation attempt failed. Deliberately a
   * separate field rather than a `'failed'` status: nine call sites branch on
   * `status === 'active' | 'completed'` (retry affordance, step navigation,
   * skip logic), so a sixth status would silently disable all of them. The
   * status still says where the step *is*; `error` says what went wrong there.
   */
  error?: string
  generatedEmail?: GeneratedEmail
  generatedDeck?: GeneratedDeck
  replyScenario?: ReplyScenario
  branchChoice?: string
  emailChainSnapshot: PreviousEmail[]
  usageLog?: UsageEntry[]
}

export interface PlaygroundState {
  steps: PlaygroundStep[]
  // The live simulation frontier — the step the walker acts on when moving forward.
  currentStepIndex: number
  // The step currently shown in the detail panel. Equals currentStepIndex during
  // normal forward play; can point at an earlier completed step when the user
  // navigates back to review or re-run it.
  viewingStepIndex: number
  contactId: number | null
  emailChain: PreviousEmail[]
  isComplete: boolean
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: 'SET_CONTACT'; contactId: number }
  | { type: 'INIT_STEPS'; steps: PlaygroundStep[] }
  | { type: 'START_GENERATING'; index: number }
  | { type: 'GENERATION_FAILED'; index: number; message?: string }
  | { type: 'COMPLETE_EMAIL_STEP'; index: number; email: GeneratedEmail; chainEntry: PreviousEmail }
  | { type: 'RERUN_EMAIL_STEP'; index: number; email: GeneratedEmail; chainEntry: PreviousEmail }
  | { type: 'COMPLETE_DECK_STEP'; index: number; deck: GeneratedDeck }
  | { type: 'RERUN_DECK_STEP'; index: number; deck: GeneratedDeck }
  | { type: 'COMPLETE_STEP'; index: number }
  | { type: 'SET_REPLY_SCENARIO'; index: number; scenario: ReplyScenario | undefined }
  | { type: 'ADVANCE'; newSteps: PlaygroundStep[]; skippedSteps: PlaygroundStep[] }
  | { type: 'BRANCH_SELECTED'; index: number; choice: string; nextSteps: PlaygroundStep[]; skippedSteps: PlaygroundStep[] }
  | { type: 'GO_TO_STEP'; index: number }
  | { type: 'TRUNCATE_FORWARD'; index: number }
  | { type: 'SIMULATION_COMPLETE' }
  | { type: 'RESET' }

const initialState: PlaygroundState = {
  steps: [],
  currentStepIndex: -1,
  viewingStepIndex: -1,
  contactId: null,
  emailChain: [],
  isComplete: false,
}

// Rebuild the flat previous-email chain from the completed sendEmail steps that
// remain, in order. Used after truncation so subsequent forward generation sees
// the correct sequence context.
function rebuildEmailChain(steps: PlaygroundStep[]): PreviousEmail[] {
  const chain: PreviousEmail[] = []
  for (const s of steps) {
    if (s.nodeType === 'sendEmail' && s.status === 'completed' && s.generatedEmail) {
      chain.push({
        subject: s.generatedEmail.subject,
        body: s.generatedEmail.body,
        step: chain.length + 1,
      })
    }
  }
  return chain
}

function reducer(state: PlaygroundState, action: Action): PlaygroundState {
  switch (action.type) {
    case 'SET_CONTACT':
      return { ...state, contactId: action.contactId }

    case 'INIT_STEPS': {
      const activeIndex = action.steps.findIndex(s => s.status === 'active')
      return {
        ...state,
        steps: action.steps,
        currentStepIndex: activeIndex,
        viewingStepIndex: activeIndex,
        isComplete: false,
        emailChain: [],
      }
    }

    case 'START_GENERATING': {
      const steps = [...state.steps]
      // Clear any previous failure — the attempt in flight owns the outcome.
      steps[action.index] = { ...steps[action.index], status: 'generating', error: undefined }
      return { ...state, steps }
    }

    case 'GENERATION_FAILED': {
      const steps = [...state.steps]
      const s = steps[action.index]
      // A failed RE-run of an already-completed step returns to 'completed'
      // (its prior content is still valid); a first-time failure returns to
      // 'active' so the user can retry. Either way the reason is recorded on
      // `error`, which is what the timeline and detail panel render — the
      // status alone can't distinguish "done" from "done, then a failed retry".
      const restored = (s.generatedEmail || s.generatedDeck) ? 'completed' : 'active'
      steps[action.index] = { ...s, status: restored, error: action.message }
      return { ...state, steps }
    }

    case 'COMPLETE_EMAIL_STEP': {
      const steps = [...state.steps]
      steps[action.index] = {
        ...steps[action.index],
        status: 'completed',
        error: undefined,
        generatedEmail: action.email,
        usageLog: action.email.usage_log,
      }
      const emailChain = [...state.emailChain, action.chainEntry]
      return { ...state, steps, emailChain }
    }

    case 'RERUN_EMAIL_STEP': {
      const steps = [...state.steps]
      steps[action.index] = {
        ...steps[action.index],
        status: 'completed',
        error: undefined,
        generatedEmail: action.email,
        usageLog: action.email.usage_log,
      }
      // Replace the corresponding entry in the flat chain (matched by sequence
      // number) so any later forward generation uses the regenerated content.
      const emailChain = state.emailChain.map(e =>
        e.step === action.chainEntry.step ? action.chainEntry : e
      )
      return { ...state, steps, emailChain }
    }

    case 'COMPLETE_DECK_STEP': {
      const steps = [...state.steps]
      steps[action.index] = {
        ...steps[action.index],
        status: 'completed',
        error: undefined,
        generatedDeck: action.deck,
        usageLog: action.deck.usage_log,
      }
      return { ...state, steps }
    }

    case 'RERUN_DECK_STEP': {
      const steps = [...state.steps]
      steps[action.index] = {
        ...steps[action.index],
        status: 'completed',
        error: undefined,
        generatedDeck: action.deck,
        usageLog: action.deck.usage_log,
      }
      return { ...state, steps }
    }

    case 'COMPLETE_STEP': {
      const steps = [...state.steps]
      steps[action.index] = { ...steps[action.index], status: 'completed', error: undefined }
      return { ...state, steps }
    }

    case 'SET_REPLY_SCENARIO': {
      const steps = [...state.steps]
      steps[action.index] = { ...steps[action.index], replyScenario: action.scenario }
      return { ...state, steps }
    }

    case 'ADVANCE': {
      const allSteps = [...state.steps, ...action.skippedSteps, ...action.newSteps]
      const nextActive = allSteps.findIndex(
        (s, i) => i >= state.steps.length + action.skippedSteps.length && s.status === 'active'
      )
      const currentStepIndex = nextActive >= 0 ? nextActive : state.currentStepIndex
      return {
        ...state,
        steps: allSteps,
        currentStepIndex,
        viewingStepIndex: currentStepIndex,
        isComplete: action.newSteps.length === 0 && action.skippedSteps.length === 0,
      }
    }

    case 'BRANCH_SELECTED': {
      const steps = [...state.steps]
      steps[action.index] = { ...steps[action.index], status: 'completed', error: undefined, branchChoice: action.choice }
      const allSteps = [...steps, ...action.skippedSteps, ...action.nextSteps]
      const nextActive = allSteps.findIndex(
        (s, i) => i >= steps.length + action.skippedSteps.length && s.status === 'active'
      )
      const currentStepIndex = nextActive >= 0 ? nextActive : state.currentStepIndex
      return {
        ...state,
        steps: allSteps,
        currentStepIndex,
        viewingStepIndex: currentStepIndex,
        isComplete: action.nextSteps.length === 0,
      }
    }

    case 'GO_TO_STEP':
      return { ...state, viewingStepIndex: action.index }

    case 'TRUNCATE_FORWARD': {
      // Drop every step after `index` and make `index` the live frontier again.
      // The step keeps its own generated content (email/deck) but any branch
      // choice — and any stale failure — is cleared so the user can re-decide
      // the path forward from a clean slate.
      const kept = state.steps.slice(0, action.index + 1)
      kept[action.index] = { ...kept[action.index], status: 'active', error: undefined, branchChoice: undefined }
      return {
        ...state,
        steps: kept,
        currentStepIndex: action.index,
        viewingStepIndex: action.index,
        emailChain: rebuildEmailChain(kept),
        isComplete: false,
      }
    }

    case 'SIMULATION_COMPLETE':
      return { ...state, isComplete: true }

    case 'RESET':
      return { ...initialState, contactId: state.contactId }

    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Graph helpers
// ---------------------------------------------------------------------------

function buildForwardAdj(edges: WorkflowEdge[]): Map<string, WorkflowEdge[]> {
  const adj = new Map<string, WorkflowEdge[]>()
  for (const e of edges) {
    if (!adj.has(e.source)) adj.set(e.source, [])
    adj.get(e.source)!.push(e)
  }
  return adj
}

function collectDescendants(
  startId: string,
  forwardAdj: Map<string, WorkflowEdge[]>,
  collected: Set<string>,
) {
  if (collected.has(startId)) return
  collected.add(startId)
  for (const edge of forwardAdj.get(startId) || []) {
    collectDescendants(edge.target, forwardAdj, collected)
  }
}

function createStep(
  node: WorkflowNode,
  _forwardAdj: Map<string, WorkflowEdge[]>,
  emailChain: PreviousEmail[],
): PlaygroundStep {
  return {
    nodeId: node.id,
    nodeType: node.type,
    nodeData: node.data,
    status: 'pending',
    emailChainSnapshot: [...emailChain],
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function usePlaygroundWalker(workflow: Workflow | null) {
  const [state, dispatch] = useReducer(reducer, initialState)

  const { nodeMap, forwardAdj, triggerNode, totalSendEmails } = useMemo(() => {
    if (!workflow) return { nodeMap: new Map<string, WorkflowNode>(), forwardAdj: new Map<string, WorkflowEdge[]>(), triggerNode: null as WorkflowNode | null, totalSendEmails: 0 }
    const nm = new Map(workflow.nodes.map(n => [n.id, n]))
    const fa = buildForwardAdj(workflow.edges)
    const tn = workflow.nodes.find(n => n.type === 'trigger') || null
    const tse = workflow.nodes.filter(n => n.type === 'sendEmail').length
    return { nodeMap: nm, forwardAdj: fa, triggerNode: tn, totalSendEmails: tse }
  }, [workflow])

  const setContact = useCallback((contactId: number) => {
    dispatch({ type: 'SET_CONTACT', contactId })
  }, [])

  const startSimulation = useCallback(() => {
    if (!triggerNode) {
      toast.error('This workflow has no trigger node')
      return
    }

    // Trigger step (auto-completed)
    const triggerStep = createStep(triggerNode, forwardAdj, [])
    triggerStep.status = 'completed'

    // Find first real node
    const firstEdges = forwardAdj.get(triggerNode.id) || []
    const firstSteps: PlaygroundStep[] = []

    for (const edge of firstEdges) {
      const node = nodeMap.get(edge.target)
      if (!node) continue
      const step = createStep(node, forwardAdj, [])
      step.status = firstSteps.length === 0 ? 'active' : 'pending'
      firstSteps.push(step)
    }

    if (firstSteps.length === 0) {
      toast.error('Workflow has no nodes connected to the trigger')
      return
    }

    dispatch({ type: 'INIT_STEPS', steps: [triggerStep, ...firstSteps] })
  }, [triggerNode, forwardAdj, nodeMap])

  // Run (or re-run) generation for the step at `index`. On a re-run of an
  // already-completed step we use that step's own emailChainSnapshot for
  // sequence context (not the live chain, which may include later emails) and
  // replace its content in place rather than appending to the chain.
  const generateStepAtIndex = useCallback(async (index: number, opts: { rerun: boolean }) => {
    const step = state.steps[index]
    if (!step || !state.contactId) return

    const chain = opts.rerun ? step.emailChainSnapshot : state.emailChain

    dispatch({ type: 'START_GENERATING', index })

    try {
      if (step.nodeType === 'sendEmail') {
        const aiConfig = step.nodeData.ai_config
        const isAI = aiConfig?.generation_mode === 'ai'

        if (isAI && aiConfig) {
          const emailStep = chain.length + 1
          const res = await workflowsApi.previewEmail({
            material_ids: aiConfig.material_ids || [],
            contact_fields: (aiConfig.contact_fields || []) as string[],
            tone: aiConfig.tone || 'professional',
            purpose: aiConfig.purpose || 'introduction',
            custom_instructions: aiConfig.custom_instructions || '',
            contact_id: state.contactId,
            email_step: emailStep,
            total_emails: totalSendEmails,
            previous_emails: chain.length > 0 ? chain : undefined,
            reply_scenario: step.replyScenario,
            workflow_id: workflow?.id,
            node_id: step.nodeId,
            template_id: step.nodeData.template_id,
          })

          dispatch({
            type: opts.rerun ? 'RERUN_EMAIL_STEP' : 'COMPLETE_EMAIL_STEP',
            index,
            email: res.data,
            chainEntry: { subject: res.data.subject, body: res.data.body, step: emailStep },
          })
        } else {
          // Manual mode — render {{contact.*}} / {{sender.*}} / {{account.*}}
          // variables via the backend (same engine the campaign send uses) so
          // the playground preview matches what the recipient will receive.
          const emailStep = chain.length + 1
          const rawSubject = step.nodeData.subject || ''
          const rawBody = step.nodeData.body || ''

          let renderedSubject = rawSubject
          let renderedBody = rawBody
          try {
            const res = await workflowsApi.renderTemplatePreview({
              subject: rawSubject,
              body: rawBody,
              contact_id: state.contactId,
            })
            renderedSubject = res.data.subject
            renderedBody = res.data.body
          } catch {
            // Fall back to raw text — better to show something than to fail
            // the playground over a transient render error.
          }

          const email: GeneratedEmail = {
            subject: renderedSubject || '(No subject)',
            body: renderedBody || '(No body)',
            email_step: emailStep,
            total_emails: totalSendEmails,
          }
          dispatch({
            type: opts.rerun ? 'RERUN_EMAIL_STEP' : 'COMPLETE_EMAIL_STEP',
            index,
            email,
            chainEntry: { subject: email.subject, body: email.body, step: emailStep },
          })
        }
      } else if (step.nodeType === 'generateDeck') {
        const config = step.nodeData.generate_deck_config
        if (!config) {
          const message = 'No deck configuration found on this node'
          toast.error(message)
          dispatch({ type: 'GENERATION_FAILED', index, message })
          return
        }

        // Attach Direct: no LLM — just resolve the picked material for preview.
        if (config.mode === 'attach_direct') {
          if (!config.attached_material_id) {
            const message = 'No material selected. Pick one in the node config.'
            toast.error(message)
            dispatch({ type: 'GENERATION_FAILED', index, message })
            return
          }
          const mres = await materialsApi.getById(config.attached_material_id)
          const m = mres.data
          dispatch({
            type: opts.rerun ? 'RERUN_DECK_STEP' : 'COMPLETE_DECK_STEP',
            index,
            deck: {
              title: m.title || m.file_name || 'Attached Material',
              slides: [],
              attached_material_id: m.id,
              content_type: m.content_type,
              filename: m.file_name,
            },
          })
          return
        }

        // /preview is now an async job — start it and poll until done.
        // Progress toasts replace in place via the shared id.
        const res = await presentationsApi.previewAndPoll(
          {
            material_ids: config.material_ids || [],
            contact_fields: (config.contact_fields || []) as string[],
            slide_count: config.slide_count || 6,
            content_focus: config.content_focus || 'pitch_deck',
            tone: config.tone || 'professional',
            custom_instructions: config.custom_instructions || '',
            contact_id: state.contactId,
            color_theme: config.color_theme,
            custom_colors: config.custom_colors,
            logo_url: config.logo_url,
          },
          (msg) => toast.info(msg, { id: 'deck-preview-progress' }),
        )

        dispatch({
          type: opts.rerun ? 'RERUN_DECK_STEP' : 'COMPLETE_DECK_STEP',
          index,
          deck: { ...res.data, logo_url: config.logo_url },
        })
      }
    } catch (error: any) {
      const message = playgroundErrorMessage(error)
      toast.error(message)
      dispatch({ type: 'GENERATION_FAILED', index, message })
    }
  }, [state, totalSendEmails, workflow])

  const generateCurrentStep = useCallback(
    () => generateStepAtIndex(state.currentStepIndex, { rerun: false }),
    [generateStepAtIndex, state.currentStepIndex],
  )

  const reRunStep = useCallback(
    (index: number) => generateStepAtIndex(index, { rerun: true }),
    [generateStepAtIndex],
  )

  // Set the reply scenario on the step currently being viewed (the frontier
  // during normal play, or an earlier step when the user has navigated back).
  const setReplyScenario = useCallback((scenario: ReplyScenario | undefined) => {
    dispatch({ type: 'SET_REPLY_SCENARIO', index: state.viewingStepIndex, scenario })
  }, [state.viewingStepIndex])

  const advanceToNext = useCallback(() => {
    const step = state.steps[state.currentStepIndex]
    if (!step) return

    const outgoing = forwardAdj.get(step.nodeId) || []
    if (outgoing.length === 0) {
      dispatch({ type: 'SIMULATION_COMPLETE' })
      return
    }

    const newSteps: PlaygroundStep[] = []
    const skippedSteps: PlaygroundStep[] = []

    for (const edge of outgoing) {
      const node = nodeMap.get(edge.target)
      if (!node) continue

      const nextStep = createStep(node, forwardAdj, state.emailChain)

      // Check follow_up_context for sendEmail nodes
      const followUpContext = (node.data as any).follow_up_context as string | undefined
      if (followUpContext && step.nodeType === 'sendEmail') {
        const simReply = step.replyScenario || 'no_reply'
        const shouldSend = checkFollowUpMatch(followUpContext, simReply)
        if (!shouldSend) {
          nextStep.status = 'skipped'
          skippedSteps.push(nextStep)
          // Also skip all descendants of this branch
          const descendants = new Set<string>()
          collectDescendants(node.id, forwardAdj, descendants)
          descendants.delete(node.id)
          for (const descId of descendants) {
            const descNode = nodeMap.get(descId)
            if (descNode) {
              const ds = createStep(descNode, forwardAdj, state.emailChain)
              ds.status = 'skipped'
              skippedSteps.push(ds)
            }
          }
          continue
        }
      }

      nextStep.status = newSteps.length === 0 ? 'active' : 'pending'
      newSteps.push(nextStep)
    }

    dispatch({ type: 'ADVANCE', newSteps, skippedSteps })

    if (newSteps.length === 0 && skippedSteps.length === 0) {
      dispatch({ type: 'SIMULATION_COMPLETE' })
    }
  }, [state, forwardAdj, nodeMap])

  const selectBranch = useCallback((choice: string) => {
    // Use viewingStepIndex, not currentStepIndex: they're identical during
    // normal forward play, but when re-routing a past step (see
    // WorkflowPlaygroundModal.handleSelectBranch) this fires from a stale
    // closure captured before the preceding truncateForward() dispatch —
    // state.currentStepIndex there would still be the old (further-along)
    // frontier, no longer valid against the just-truncated steps array.
    // viewingStepIndex is exactly the index truncateForward makes
    // authoritative, so it stays in range either way.
    const index = state.viewingStepIndex
    const step = state.steps[index]
    if (!step) return

    const outgoing = forwardAdj.get(step.nodeId) || []
    const chosenEdge = outgoing.find(e => (e.sourceHandle || '').toLowerCase() === choice.toLowerCase())
    const skippedEdges = outgoing.filter(e => (e.sourceHandle || '').toLowerCase() !== choice.toLowerCase())

    // Collect all skipped descendants
    const allSkippedIds = new Set<string>()
    for (const edge of skippedEdges) {
      collectDescendants(edge.target, forwardAdj, allSkippedIds)
    }
    const skippedSteps: PlaygroundStep[] = []
    for (const id of allSkippedIds) {
      const node = nodeMap.get(id)
      if (node) {
        const s = createStep(node, forwardAdj, state.emailChain)
        s.status = 'skipped'
        skippedSteps.push(s)
      }
    }

    // Create next step on chosen branch
    const nextSteps: PlaygroundStep[] = []
    if (chosenEdge) {
      const node = nodeMap.get(chosenEdge.target)
      if (node) {
        const ns = createStep(node, forwardAdj, state.emailChain)
        ns.status = 'active'
        nextSteps.push(ns)
      }
    }

    dispatch({
      type: 'BRANCH_SELECTED',
      index,
      choice,
      nextSteps,
      skippedSteps,
    })
  }, [state, forwardAdj, nodeMap])

  const completeAndAdvance = useCallback(() => {
    dispatch({ type: 'COMPLETE_STEP', index: state.currentStepIndex })
    // Small delay to let reducer run, then advance
    setTimeout(() => advanceToNext(), 0)
  }, [state.currentStepIndex, advanceToNext])

  // --- Back-navigation ---

  // Jump the detail panel to a previously completed (or the active) step.
  const goToStep = useCallback((index: number) => {
    const step = state.steps[index]
    if (!step) return
    if (step.status !== 'completed' && step.status !== 'active') return
    dispatch({ type: 'GO_TO_STEP', index })
  }, [state.steps])

  const backToCurrent = useCallback(() => {
    dispatch({ type: 'GO_TO_STEP', index: state.currentStepIndex })
  }, [state.currentStepIndex])

  // Drop every step after `index` and make it the frontier again (used when a
  // re-run changes the path — a different branch or reply scenario).
  const truncateForward = useCallback((index: number) => {
    dispatch({ type: 'TRUNCATE_FORWARD', index })
  }, [])

  // True when the reply scenario chosen on the sendEmail node `nodeId` gates a
  // downstream follow-up (i.e. changing the reply would change which later steps
  // run). Used to decide whether a reply change is a path change.
  const replyGatesDownstream = useCallback((nodeId: string): boolean => {
    const descendants = new Set<string>()
    collectDescendants(nodeId, forwardAdj, descendants)
    descendants.delete(nodeId)
    for (const id of descendants) {
      const node = nodeMap.get(id)
      if (node && (node.data as any).follow_up_context) return true
    }
    return false
  }, [forwardAdj, nodeMap])

  const downloadDeck = useCallback(async (stepIndex: number, format: 'pdf' | 'pptx') => {
    const step = state.steps[stepIndex]
    if (!step || step.nodeType !== 'generateDeck' || !state.contactId) return
    const config = step.nodeData.generate_deck_config
    if (!config) return

    const label = format.toUpperCase()
    const generatedDeck = step.generatedDeck
    if (!generatedDeck) {
      toast.error('No deck generated yet for this step.')
      return
    }
    try {
      toast.info(`Rendering ${label}...`, { id: 'deck-progress' })
      // Reuse the deck slides already produced by the playground's
      // preview step — render-slides is a pure file build (no LLM).
      const res = await presentationsApi.renderSlides({
        title: generatedDeck.title || 'Presentation',
        slides: generatedDeck.slides || [],
        color_theme: generatedDeck.color_theme as Record<string, string> | undefined,
        output_format: format,
      })

      if (res.data.download_url) {
        // Use blob URL approach for authenticated download
        try {
          const blob = await presentationsApi.fetchFileBlob(res.data.download_url)
          const blobUrl = URL.createObjectURL(blob)
          const a = document.createElement('a')
          a.href = blobUrl
          a.download = res.data.filename || `presentation.${format}`
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(blobUrl)
        } catch {
          // Fallback: open in new tab
          const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api').replace('/api', '')
          window.open(`${baseUrl}${res.data.download_url}`, '_blank')
        }
        toast.success(`${label} file ready for download`)

        // Store download_url and filename on the deck for attachment support
        if (step.generatedDeck) {
          dispatch({
            type: 'RERUN_DECK_STEP',
            index: stepIndex,
            deck: { ...step.generatedDeck, download_url: res.data.download_url, filename: res.data.filename },
          })
        }
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || `Failed to generate ${label}`)
    }
  }, [state])

  // Convenience wrappers
  const downloadDeckPdf = useCallback((stepIndex: number) => downloadDeck(stepIndex, 'pdf'), [downloadDeck])
  const downloadDeckPptx = useCallback((stepIndex: number) => downloadDeck(stepIndex, 'pptx'), [downloadDeck])

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  return {
    state,
    setContact,
    startSimulation,
    generateCurrentStep,
    reRunStep,
    setReplyScenario,
    advanceToNext,
    selectBranch,
    completeAndAdvance,
    goToStep,
    backToCurrent,
    truncateForward,
    replyGatesDownstream,
    downloadDeckPdf,
    downloadDeckPptx,
    reset,
    hasTrigger: !!triggerNode,
    totalSendEmails,
  }
}

// ---------------------------------------------------------------------------
// follow_up_context matching (mirrors backend _should_send_follow_up)
// ---------------------------------------------------------------------------

function checkFollowUpMatch(followUpContext: string, simReply: ReplyScenario): boolean {
  if (followUpContext === 'no_reply') return simReply === 'no_reply'
  if (followUpContext === 'positive') return simReply === 'positive'
  if (followUpContext === 'not_interested') return simReply === 'not_interested' || simReply === 'negative'
  return true
}
